import { useState } from 'react';
import { api } from '../api';
import { Card, useToast } from './../components/ui';
import { KitjaLogo } from '../components/KitjaLogo';

type MemberType = 'adult' | 'child' | 'aupair';
interface DraftMember {
  name: string;
  member_type: MemberType;
}

const SHOPS = [
  { type: 'supermarket', label: 'Supermarket', hint: 'The main shop — the week is built around it' },
  { type: 'boulangerie', label: 'Boulangerie', hint: 'Bread and viennoiserie, eaten the same morning' },
  { type: 'butcher', label: 'Butcher', hint: 'Good cuts of meat' },
  { type: 'fishmonger', label: 'Fishmonger', hint: 'Fresh fish' },
  { type: 'market', label: 'Market', hint: 'Fruit and vegetables' },
] as const;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * First run (§7).
 *
 * Two things cannot be guessed. Who eats here decides how much of everything
 * gets cooked and bought, and when the main shop happens decides where the
 * week starts and how long the fresh food has to last. Everything else in
 * Kitja has a defensible default; these two would just be wrong.
 *
 * Deliberately short. Nobody signs up to fill in a form, and the rest can be
 * corrected in Settings once there is a week on the screen to react to.
 */
export function SetupPage({ householdName, onDone }: { householdName: string; onDone: () => void }) {
  const toast = useToast();
  const [members, setMembers] = useState<DraftMember[]>([
    { name: '', member_type: 'adult' },
    { name: '', member_type: 'adult' },
  ]);
  const [mainShop, setMainShop] = useState(5); // Saturday
  const [extras, setExtras] = useState<Record<string, number | null>>({});
  const [busy, setBusy] = useState(false);

  const named = members.filter((m) => m.name.trim().length > 0);

  const save = async () => {
    setBusy(true);
    try {
      await api.completeSetup({
        members: named.map((m) => ({ name: m.name.trim(), member_type: m.member_type })),
        shoppingDays: [
          { weekday: mainShop, shop_type: 'supermarket', required: true },
          ...Object.entries(extras)
            .filter(([, weekday]) => weekday !== null)
            .map(([shop_type, weekday]) => ({ weekday: weekday as number, shop_type })),
        ],
      });
      onDone();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save that');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="main" style={{ maxWidth: 620 }}>
      <div className="stack" style={{ marginTop: '6vh' }}>
        <div style={{ display: 'grid', justifyItems: 'center', gap: 10, marginBottom: 4 }}>
          <KitjaLogo size={56} />
          <h1 style={{ margin: 0 }}>Welcome to {householdName}</h1>
          <p className="muted" style={{ margin: 0, textAlign: 'center' }}>
            Two questions, and Kitja can plan you a week. Everything else you can change later.
          </p>
        </div>

        <Card title="Who eats here?">
          <p className="dim" style={{ marginBottom: 12 }}>
            Quantities are scaled to whoever is actually at the table, so this decides how much of
            everything gets cooked and bought.
          </p>
          <div className="stack" style={{ gap: 8 }}>
            {members.map((member, index) => (
              <div key={index} className="person-row">
                <input
                  value={member.name}
                  placeholder="Name"
                  onChange={(event) =>
                    setMembers((current) =>
                      current.map((m, i) => (i === index ? { ...m, name: event.target.value } : m)),
                    )
                  }
                />
                <select
                  value={member.member_type}
                  onChange={(event) =>
                    setMembers((current) =>
                      current.map((m, i) =>
                        i === index ? { ...m, member_type: event.target.value as MemberType } : m,
                      ),
                    )
                  }
                >
                  <option value="adult">Adult</option>
                  <option value="child">Child</option>
                  <option value="aupair">Au pair</option>
                </select>
                {members.length > 1 && (
                  <button
                    type="button"
                    className="btn small"
                    onClick={() => setMembers((c) => c.filter((_, i) => i !== index))}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn small"
            style={{ marginTop: 10 }}
            onClick={() => setMembers((c) => [...c, { name: '', member_type: 'child' }])}
          >
            Add someone
          </button>
        </Card>

        <Card title="When do you do the big shop?">
          <p className="dim" style={{ marginBottom: 12 }}>
            Your week runs from one main shop to the next, rather than Monday to Sunday — so this
            is also where your planning week starts.
          </p>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {DAYS.map((day, weekday) => (
              <button
                key={day}
                type="button"
                className={`btn small${mainShop === weekday ? ' on' : ''}`}
                aria-pressed={mainShop === weekday}
                onClick={() => setMainShop(weekday)}
              >
                {day.slice(0, 3)}
              </button>
            ))}
          </div>

          <p className="dim" style={{ margin: '18px 0 8px' }}>
            Anywhere else you shop regularly? Leave these alone if not — you can add them later.
          </p>
          <div className="stack" style={{ gap: 6 }}>
            {SHOPS.filter((shop) => shop.type !== 'supermarket').map((shop) => (
              <div key={shop.type} className="setup-row">
                <span className="setup-label" title={shop.hint}>
                  {shop.label}
                </span>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={`btn small${extras[shop.type] == null ? ' on' : ''}`}
                    onClick={() => setExtras((c) => ({ ...c, [shop.type]: null }))}
                  >
                    Never
                  </button>
                  {DAYS.map((day, weekday) => (
                    <button
                      key={day}
                      type="button"
                      className={`btn small${extras[shop.type] === weekday ? ' on' : ''}`}
                      onClick={() => setExtras((c) => ({ ...c, [shop.type]: weekday }))}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="row">
          <button
            className="btn primary"
            disabled={busy || named.length === 0}
            onClick={() => void save()}
          >
            {busy ? 'Setting up…' : 'Plan my first week'}
          </button>
          {named.length === 0 && <span className="dim">Add at least one person.</span>}
        </div>
      </div>
    </div>
  );
}
